<script lang="ts">
    import { scale } from "svelte/transition"

    interface Props {
        show?: boolean
        size?: string
        cssClass?: string
        extendedHeight?: boolean
        navigation?: import("svelte").Snippet
        imagePreview?: import("svelte").Snippet
        closeModal?: () => void
    }

    let {
        show = false,
        size = "md",
        cssClass = "",
        extendedHeight = false,
        navigation = null,
        imagePreview = null,
        closeModal,
    }: Props = $props()

    // Reference to the <dialog> so we can call .showModal() / .close()
    let dialogRef: HTMLDialogElement | null = null

    // Closes the dialog when the user clicks on the backdrop or presses Escape
    const clickOnBackground = () => {
        show = false
        closeModal?.()
    }

    // Watch `show` and open/close <dialog> as needed
    $effect(() => {
        if (dialogRef) {
            if (show && !dialogRef.open) {
                dialogRef.showModal()
            } else if (!show && dialogRef.open) {
                dialogRef.close()
            }
        }
    })
</script>

<dialog
    class="modal-wrapper {cssClass}"
    bind:this={dialogRef}
    onclick={(e) => {
        if (e.target === dialogRef) clickOnBackground()
    }}
    onkeydown={(e) => e.key === "Escape" && clickOnBackground()}
>
    <div
        class="modal modal-{size}"
        role="document"
        transition:scale
        class:extendedHeight
        onclick={(e) => e.stopPropagation()}
    >
        {#if imagePreview}
            <div class="image-preview">
                {@render imagePreview?.()}
            </div>
        {/if}

        <div class="navigation">
            {@render navigation?.()}
        </div>
    </div>
</dialog>

<style lang="less">
    .modal-wrapper {
        border: none;
        padding: 0;
        margin: 0;
        z-index: 100;
        position: fixed;
        display: flex;
        justify-content: center;
        top: 0;
        width: 100vw;
        min-width: 100vw;
        padding: 0px var(--default-padding);
        height: 100vh;
        right: 0;
        bottom: 0;
        left: 0;
        &::backdrop {
            background-color: rgba(0, 0, 0, 0.5);
            transition: all 0.2s ease-in-out;
        }
        .modal {
            display: flex;
            gap: 1.2rem;
            width: 100%;
            height: fit-content;
            margin-top: 128px;
            max-width: var(--extended-max-width);
            & > div {
                border-radius: 4px;
                box-shadow: 0px 0px 20px 0px #242220;
                background-color: var(--bg-100);
                min-height: 480px;
                height: 480px;

                &:first-child {
                    flex-grow: 3;
                    width: 0px;
                }
                &:last-child {
                    width: 0px;
                    flex-grow: 5;
                }
            }
            &.extendedHeight {
                & > div {
                    min-height: unset;
                    height: unset;
                }
            }
        }
    }
</style>
